import React, { useState, useEffect } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import startCase from "lodash/startCase";
import Filters from "./Filters";
import * as calFns from "../../components/Calendar/functions/calendarFunctions";
import useMobileScreen from "../../hooks/useMobileScreen";
import List, { EmptyView } from "../../components/OList";
import { getContactFormSubmissions } from "../../api/reports";
import "./Reports.scss";
import PageControl from "../../components/PageControl";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import { t } from "../../components/translate";
import { notificationAdd } from "../../actions/notification";

export enum CONTACT_FORM_SUBMISSION_SOURCE {
  OKHATI = "OKHATI",
  NEPAL_LAND = "NEPAL_LAND"
}

const ContactFormSubmissionReport = (): React.ReactNode => {
  const pageSize = 50;

  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  // local states
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [reportData, setReportData] = useState([]);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [filters, setFilters] = useState({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    contactFormSubmissionSource: null
  });

  // effects
  useEffect(() => {
    (async () => {
      try {
        setIsRequestLoading(true);
        const data = await getContactFormSubmissions({
          ...filters,
          page,
          pageSize,
          source: filters.contactFormSubmissionSource
        });
        setTotal(data.total);
        setReportData(data.results);
      } catch (err: any) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: err?.data?.message || "Failed to fetch SMS report",
            autoTimeout: true
          })
        );
      } finally {
        setIsRequestLoading(false);
      }
    })();
  }, [filters, page]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ px: 3 }}>
        <Filters
          filters={filters}
          onSetFilters={(newfilters) => {
            setFilters(newfilters);
            setPage(0);
          }}
          isRequestLoading={isRequestLoading}
        />
      </Box>

      <Box
        className="smsReportList"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
          overflowX: "auto",
          height: "calc(100vh - 260px)"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1100px" : "auto" }}>
          <List
            automation="smsReportList"
            data={reportData}
            rowHeight={40}
            defaultSortColumn="clientName"
            defaultSortOrder={-1}
            activeRow={0}
            adjustHeightToContents
            columns={[
              {
                key: "createdAt",
                label: "Created At",
                formatter: (row) => (
                  <Typography>
                    {calFns.convertADtoBS(new Date(row.created_at)).formatted}
                  </Typography>
                )
              },
              {
                key: "name",
                label: `${t("Name")}`,
                formatter: (row) => <Typography>{row.name}</Typography>
              },
              {
                key: "message",
                label: "Message",
                formatter: (row) => (
                  <Tooltip title={row.message || ""}>
                    <Typography>{(row.message || "").substring(0, 20)}...</Typography>
                  </Tooltip>
                )
              },
              {
                key: "phone",
                label: `${t("msgReport.PhNo.")}`,
                formatter: (row) => <Typography>{row.phone}</Typography>
              },
              {
                key: "email",
                label: `${t("email")}`,
                formatter: (row) => <Typography>{row.email}</Typography>
              },
              {
                key: "inquiryType",
                label: "Inquiry Type",
                formatter: (row) => <Typography>{row.inquiryType}</Typography>
              },
              {
                key: "source",
                label: "Source",
                formatter: (row) => <Typography>{startCase(row.source || "")}</Typography>
              }
            ]}
          >
            <EmptyView>
              <Box textAlign="center" padding="50px">
                {t("msgReport.noItemsToShow")}
              </Box>
            </EmptyView>
          </List>
        </Box>
      </Box>
      <Box className="labNavigatePage">
        <PageControl
          page={page}
          pageSize={pageSize}
          onSetPage={(v) => {
            setPage(v);
          }}
          maximumDataCount={total}
        />
      </Box>
    </Box>
  );
};

export default ContactFormSubmissionReport;
